import { type InvoiceType } from '@goshippo/libraries/shared/InvoicesTable';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Info } from '@mui/icons-material';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import capitalize from 'lodash-es/capitalize';
import * as moment from 'moment';
import { useTranslation } from 'react-i18next';

import type InvoiceAdjustment from '~/types/InvoiceAdjustment';

import Table, { type Column } from '~/components/Table';
import { CARRIERS_WITH_GST } from '~/constants/carriers';
import { currencies } from '~/constants/currency';
import { colors, fontWeight } from '~/styles/constants';

import { type InvoiceItemType } from '../types/invoice.type';
import { InvoiceVatMessage } from './InvoiceVatMessage';

const commonTableHeaderCellStyle = css`
  color: ${colors.black};
  padding: 0.625rem;
  font-size: 0.8125rem;
  line-height: 1.125rem;
`;

const TableHeaderContent = styled.div`
  ${commonTableHeaderCellStyle}
  font-size: 0.875rem;
  font-weight: ${fontWeight.medium};
`;

const TableCellContent = styled.div`
  ${commonTableHeaderCellStyle}
  font-weight: ${fontWeight.light};
`;

const noPaddingStyles = css`
  padding: 0;

  :last-of-type {
    padding-right: 0;
  }
  :first-of-type {
    padding-left: 0;
  }
`;

const rightAlignNoPaddingStyles = css`
  ${noPaddingStyles}
  text-align: right;
`;

const StyledFooterContents = styled.div`
  ${commonTableHeaderCellStyle}
  font-size: 0.875rem;
  text-align: right;
  white-space: nowrap;
`;

interface InvoiceItemsTableProps {
  invoice: InvoiceType;
  invoiceAdjustments: InvoiceAdjustment[];
  invoiceItems: InvoiceItemType[];
}

export const InvoiceItemsTable = (props: InvoiceItemsTableProps) => {
  const { invoice, invoiceAdjustments, invoiceItems } = props;
  const { t } = useTranslation(['settings', 'translations']);

  const totalCurrency = invoice.total_currency ? currencies[invoice.total_currency] : null;

  const paymentMethodUsed = invoice.payment_details
    ? invoice.payment_details.find(
        (method) => parseFloat(method.amount) === Math.abs(invoice.payment_received_total ?? 0),
      )
    : null;

  // If there is a payment_received_total, it is from new user billing payments.
  const paymentReceivedLine = invoice.payment_received_total ? (
    <tr data-testid="invoice-table-payment-received">
      <td colSpan={5}>
        <StyledFooterContents>
          <strong>
            {t('account.billing.invoices.download.invoiceItemsTable.footers.payment')}
          </strong>
          {paymentMethodUsed
            ? ` (${t('account.billing.invoices.download.invoiceItemsTable.footers.endingIn', {
                lastFour: paymentMethodUsed.last_four,
                name: capitalize(paymentMethodUsed.name),
              })})`
            : null}
        </StyledFooterContents>
      </td>
      <td>
        <StyledFooterContents>
          {t('currency', {
            ns: 'translations',
            value: {
              currency_iso: totalCurrency?.iso ?? currencies.USD.iso,
              value: invoice.payment_received_total.toFixed(2),
            },
          })}
        </StyledFooterContents>
      </td>
    </tr>
  ) : null;

  // If there are no new user billing payments and a single payment method, show payment method details here.
  const totalLineTitle =
    invoice.payment_details &&
    invoice.payment_details.length === 1 &&
    !invoice.payment_received_total
      ? t('account.billing.invoices.download.invoiceItemsTable.footers.paidWith', {
          lastFour: invoice.payment_details[0].last_four,
          name: capitalize(invoice.payment_details[0].name),
        })
      : t('account.billing.invoices.download.invoiceItemsTable.footers.total');

  const columns: Column<InvoiceItemType>[] = [
    // Date
    {
      cell: ({ data }: { data: InvoiceItemType }) => (
        <TableCellContent>{moment(data.object_created).format('l')}</TableCellContent>
      ),
      customCellStyles: noPaddingStyles,
      customHeaderStyles: noPaddingStyles,
      header: () => (
        <TableHeaderContent>
          {t('account.billing.invoices.download.invoiceItemsTable.headers.date')}
        </TableHeaderContent>
      ),
      noPadding: true,
    },
    // Item Type
    {
      cell: ({ data }: { data: InvoiceItemType }) => (
        <TableCellContent>
          <Stack alignItems="center" direction="row">
            <Box>{data.invoice_item_type}</Box>
            {data.invoice_item_type === 'ONE_OFF_CREDIT' && (
              <Tooltip
                placement="top"
                role="tooltip"
                title={
                  <Stack spacing={1}>
                    <strong>
                      {t('account.billing.invoices.download.invoiceItemsTable.footers.creditTitle')}
                    </strong>
                    <div>
                      {t('account.billing.invoices.download.invoiceItemsTable.footers.credit')}
                    </div>
                  </Stack>
                }
              >
                <Box>
                  <IconButton aria-label="More information" color="secondary" size="small">
                    <Info fontSize="small" />
                  </IconButton>
                </Box>
              </Tooltip>
            )}
          </Stack>
        </TableCellContent>
      ),
      customCellStyles: noPaddingStyles,
      customHeaderStyles: noPaddingStyles,
      header: () => (
        <TableHeaderContent>
          {t('account.billing.invoices.download.invoiceItemsTable.headers.itemType')}
        </TableHeaderContent>
      ),
      noPadding: true,
    },
    // Carrier
    {
      cell: ({ data }: { data: InvoiceItemType }) => {
        const carrier = data.transaction?.carrier || data.provider_name;
        return (
          <TableCellContent>
            {data.invoice_item_type !== 'PAYMENT_RECEIVED' && carrier ? carrier : ''}
          </TableCellContent>
        );
      },
      customCellStyles: noPaddingStyles,
      customHeaderStyles: noPaddingStyles,
      header: () => (
        <TableHeaderContent>
          {t('account.billing.invoices.download.invoiceItemsTable.headers.carrier')}
        </TableHeaderContent>
      ),
      noPadding: true,
    },
    // Tracking Number
    {
      cell: ({ data }: { data: InvoiceItemType }) => {
        const trackingNumber = data.transaction?.tracking_number || data.tracking_number;
        return data.invoice_item_type !== 'PAYMENT_RECEIVED' && trackingNumber ? (
          <TableCellContent>
            {`${trackingNumber || ''} ${data.description ? `(${data.description})` : ''} ${
              data.object_purpose === 'REFUND' ? '(refund)' : ''
            }`}
          </TableCellContent>
        ) : null;
      },
      customCellStyles: noPaddingStyles,
      customHeaderStyles: noPaddingStyles,
      header: () => (
        <TableHeaderContent>
          {t('account.billing.invoices.download.invoiceItemsTable.headers.trackingNumber')}
        </TableHeaderContent>
      ),
      noPadding: true,
    },
    // Tax
    {
      cell: ({ data }: { data: InvoiceItemType }) => {
        const invoiceCarrier = data.transaction?.carrier || data.provider_name;
        return (
          <TableCellContent>
            {CARRIERS_WITH_GST.some((carrier) => carrier.name === invoiceCarrier) &&
              t('account.billing.invoices.download.invoiceItemsTable.gstIncluded')}
          </TableCellContent>
        );
      },
      customCellStyles: rightAlignNoPaddingStyles,
      customHeaderStyles: rightAlignNoPaddingStyles,
      header: () => (
        <TableHeaderContent>
          {t('account.billing.invoices.download.invoiceItemsTable.headers.tax')}
        </TableHeaderContent>
      ),
      noPadding: true,
    },
    // Amount
    {
      cell: ({ data }: { data: InvoiceItemType }) => (
        <TableCellContent>
          {t('currency', {
            ns: 'translations',
            value: { currency_iso: data.currency, value: data.amount },
          })}
        </TableCellContent>
      ),
      customCellStyles: rightAlignNoPaddingStyles,
      customHeaderStyles: rightAlignNoPaddingStyles,
      header: () => (
        <TableHeaderContent>
          {t('account.billing.invoices.download.invoiceItemsTable.headers.amount')}
        </TableHeaderContent>
      ),
      noPadding: true,
    },
  ];

  return (
    <Table<InvoiceItemType> autoLayout columns={columns} data={invoiceItems}>
      <tfoot>
        <tr>
          <td colSpan={5}>
            <StyledFooterContents>
              <strong>
                {t('account.billing.invoices.download.invoiceItemsTable.footers.subtotal')}
              </strong>
            </StyledFooterContents>
          </td>
          <td>
            <StyledFooterContents>
              {t('currency', {
                ns: 'translations',
                value: {
                  currency_iso: totalCurrency.iso ?? currencies.USD.iso,
                  value: invoice.charge_total.toFixed(2),
                },
              })}
            </StyledFooterContents>
          </td>
        </tr>
        {invoiceAdjustments.map((adjustment, index) => (
          <tr key={index}>
            <td colSpan={5}>
              <StyledFooterContents>{adjustment.description}</StyledFooterContents>
            </td>
            <td>
              <StyledFooterContents>
                {t('currency', {
                  ns: 'translations',
                  value: {
                    currency_iso: adjustment.amountCurrency?.iso,
                    value: adjustment.amount,
                  },
                })}
              </StyledFooterContents>
            </td>
          </tr>
        ))}
        {paymentReceivedLine}
        {invoice.credit_applied_total ? (
          <tr>
            <td colSpan={5}>
              <StyledFooterContents>
                <strong>
                  {t(
                    'settings:account.billing.invoices.download.invoiceItemsTable.footers.shippoCredit',
                  )}
                </strong>
              </StyledFooterContents>
            </td>
            <td>
              <StyledFooterContents>
                {t('currency', {
                  ns: 'translations',
                  value: {
                    currency_iso: totalCurrency.iso ?? currencies.USD.iso,
                    value: invoice.credit_applied_total.toFixed(2),
                  },
                })}
              </StyledFooterContents>
            </td>
          </tr>
        ) : null}
        <tr>
          <td colSpan={5}>
            <StyledFooterContents data-testid="invoice-table-total-description">
              <strong>{totalLineTitle}</strong>
            </StyledFooterContents>
          </td>
          <td>
            <StyledFooterContents>
              <strong>
                {t('currency', {
                  ns: 'translations',
                  value: {
                    currency_iso: totalCurrency.iso ?? currencies.USD.iso,
                    value:
                      invoice.net_total_charge_amount?.toFixed(2) ||
                      invoice.total_amount.toFixed(2),
                  },
                })}
              </strong>
            </StyledFooterContents>
          </td>
        </tr>
        <InvoiceVatMessage invoiceItems={invoiceItems} />
      </tfoot>
    </Table>
  );
};
